package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.domain.image.ImageTarget
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.SignedUrlCacheEntry
import fr.manyverse.app.domain.repository.ImageStorageRepository
import fr.manyverse.app.domain.repository.SignedUrlCacheRepository
import kotlin.time.Clock
import kotlin.time.Duration.Companion.hours

// Cache expiration constants
private val SIGNED_URL_EXPIRATION = 24.hours
private val CACHE_REFRESH_BUFFER = 2.hours

/**
 * Caching decorator for ImageStorageRepository.
 * Caches signed URLs to reduce Supabase Storage API calls.
 */
class CachingImageStorageRepository(
    private val remoteRepository: ImageStorageRepository,
    private val signedUrlCache: SignedUrlCacheRepository
) : ImageStorageRepository {

    override suspend fun upload(target: ImageTarget, image: ImageData): Result<String> {
        return remoteRepository.upload(target, image).also { result ->
            result.onSuccess { invalidate(target) }
        }
    }

    override suspend fun delete(target: ImageTarget): Result<Unit> {
        return remoteRepository.delete(target).also { result ->
            result.onSuccess { invalidate(target) }
        }
    }

    override suspend fun downloadImage(imagePath: String): Result<ImageData> {
        return remoteRepository.downloadImage(imagePath)
    }

    override suspend fun uploadAnnouncementImage(tableId: String, image: ImageData): Result<String> {
        return remoteRepository.uploadAnnouncementImage(tableId, image)
    }

    override suspend fun getSignedUrl(imagePath: String, version: String?): Result<String> {
        // Check cache first
        val cachedEntry = signedUrlCache.get(imagePath)
        if (cachedEntry != null) {
            // Check if we should refresh proactively (2 hours before expiration)
            val nearExpiration = Clock.System.now() + CACHE_REFRESH_BUFFER >= cachedEntry.expiresAt
            // L'image a été remplacée depuis la mise en cache : le chemin est identique, donc
            // seule une nouvelle signature produit une URL différente (le jeton change), ce qui
            // invalide du même coup le cache du chargeur d'images.
            val staleContent = cachedEntry.version != version
            if (!nearExpiration && !staleContent) {
                return Result.success(cachedEntry.signedUrl)
            }
        }

        // Cache miss or needs refresh - fetch from remote
        return remoteRepository.getSignedUrl(imagePath, version).also { result ->
            result.onSuccess { signedUrl ->
                val expiresAt = Clock.System.now() + SIGNED_URL_EXPIRATION
                signedUrlCache.put(imagePath, SignedUrlCacheEntry(signedUrl, expiresAt, version))
            }
        }
    }

    private fun invalidate(target: ImageTarget) {
        target.candidatePaths().forEach { signedUrlCache.remove(it) }
    }
}
